// /teams/:teamId/projects/:projectId/runs — list every run in the project,
// most-recent first. Status badge mirrors FlowsList.

import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { useProject, useRuns } from '../../lib/data-hooks.js'

const STATUS_STYLES = {
  passed: 'bg-[var(--color-passed)]/15 text-[var(--color-passed)]',
  failed: 'bg-[var(--color-failed)]/15 text-[var(--color-failed)]',
  running: 'bg-[var(--color-running)]/15 text-[var(--color-running)]',
}

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] ?? 'bg-[var(--color-sandstone)] text-[var(--color-slate)]'
  const icon = status === 'passed' ? '✓' : status === 'failed' ? '✕' : '◌'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      <span aria-hidden="true">{icon}</span>
      {status ?? 'queued'}
    </span>
  )
}

function fmtTime(t) {
  if (!t) return ''
  const ms = typeof t === 'string' ? new Date(t).getTime() : (t.toMillis?.() ?? Number(t))
  if (!ms) return ''
  return new Date(ms).toLocaleString()
}

export function RunsList() {
  const { teamId, projectId } = useParams()
  const { data: project } = useProject(teamId, projectId)
  const { data: runs, loading, error } = useRuns(teamId, projectId)

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: teamId, to: `/teams/${teamId}` },
          { label: project?.name ?? projectId, to: `/teams/${teamId}/projects/${projectId}` },
          { label: 'Runs' },
        ]}
        title="Runs"
        subhead="Every flow execution in this project, most recent first."
      />

      <div className="mx-auto max-w-5xl px-6 py-8">
        {loading && <p className="text-sm text-[var(--color-schist)]">Loading runs…</p>}
        {error && (
          <p className="text-sm text-[var(--color-failed)]" role="alert">
            {error.message}
          </p>
        )}
        {!loading && runs.length === 0 && (
          <div className="rounded-md border border-dashed border-[var(--color-sandstone)] bg-[var(--color-sandstone)]/30 p-8 text-center">
            <p className="text-sm text-[var(--color-slate)]">
              No runs yet. Run a flow from the CLI (
              <code className="font-mono">cairn run &lt;flow&gt;</code>) or open a flow and click{' '}
              <strong>Re-run</strong> on its detail page.
            </p>
          </div>
        )}
        {runs.length > 0 && (
          <ul className="divide-y divide-[var(--color-sandstone)] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)]">
            {runs.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/teams/${teamId}/projects/${projectId}/runs/${r.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 hover:bg-[var(--color-sandstone)]/40"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--color-basalt)]">
                      {r.flowName ?? r.id}
                    </div>
                    <div className="font-mono text-xs text-[var(--color-schist)]">
                      {r.env ? `${r.env} · ` : ''}
                      {fmtTime(r.startedAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {r.model && (
                      <span className="font-mono text-[10px] text-[var(--color-schist)]">
                        {r.model}
                      </span>
                    )}
                    <StatusBadge status={r.status} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
